import { useAddedToBagContextStore } from "@faire/retailer-visitor-shared/components/Notifications/AddedToBag/AddedToBagStore";
import { useInvalidateCart } from "@faire/retailer-visitor-shared/features/Cart/useCart";
import {
  useCartItems,
  useInvalidateCartItems,
} from "@faire/retailer-visitor-shared/hooks/cart/useCartItems";
import { useInvalidateCartSummary } from "@faire/retailer-visitor-shared/hooks/cart/useCartSummary";
import { useIsOnCartPage } from "@faire/retailer-visitor-shared/hooks/cart/useIsOnCartPage";
import { useRetailerFinancial } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerFinancial";
import { commonThrottle } from "@faire/retailer-visitor-shared/lib/commonThrottle";
import { generateBrandCartForUpdates } from "@faire/retailer-visitor-shared/lib/updateCartV2";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import {
  ICartItemForUpdate,
  IUpdateItemsOptions,
  UpdateCartArg,
  UpdateCartItemsHandler,
} from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { UpdateCartUPLTParams } from "@faire/retailer-visitor-shared/stores/domain/CartStore/consts";
import { triggerAddToCartEventV2 } from "@faire/retailer-visitor-shared/stores/domain/CartStore/triggerAddToCartEvent";
import { useInvalidateBrandCart } from "@faire/retailer-visitor-shared/views/brand/BrandCart/hooks/useBrandCart";
import { TelemetryManager } from "@faire/web/common/opentelemetry/TelemetryManager";
import { useMutation as useCartUpdateMutation } from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/put-hooks";
import { IUpdateCartRequestV2 } from "@faire/web-api--source/faire/carts/IUpdateCartRequestV2";
import { IUpdateCartResponseV2 } from "@faire/web-api--source/faire/carts/IUpdateCartResponseV2";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import { useCallback, useMemo } from "react";

export const useCartUpdate = () => {
  const { leanCartItemMap } = useCartItems();
  const { invalidateCartItemInventory, invalidateLeanBrandsMap } =
    useInvalidateCartItems();
  const { invalidateBrandCart } = useInvalidateBrandCart();
  const { invalidateEntireCartSummary } = useInvalidateCartSummary();
  const { invalidateEntireCart } = useInvalidateCart();

  const isOnCartPage = useIsOnCartPage();
  const [, setAddedToBagStore] = useAddedToBagContextStore([]);

  const { mutateAsync: updateCartMutation, ...mutationResult } =
    useCartUpdateMutation();

  const { currency } = useRetailerFinancial();

  const handleUpdateCartSuccess = useCallback(
    (
      updateResponse: IUpdateCartResponseV2,
      upltParams: UpdateCartUPLTParams,
      skipCartRefetch?: boolean,
      request?: IUpdateCartRequestV2
    ) => {
      triggerAddToCartEventV2(
        updateResponse,
        cloneDeep(leanCartItemMap ?? {}),
        upltParams,
        currency
      );
      invalidateCartItemInventory();
      invalidateLeanBrandsMap();
      invalidateEntireCartSummary();
      if (!isEmpty(updateResponse.updated_brand_data)) {
        updateResponse.updated_brand_data.forEach((brandData) => {
          invalidateBrandCart({ brandToken: brandData.brand_token ?? "" });
        });
      } else if (request && !isEmpty(request.brand_carts)) {
        request.brand_carts.forEach((brandCart) => {
          invalidateBrandCart({ brandToken: brandCart.brand_token ?? "" });
        });
      }
      if (!skipCartRefetch) {
        // If we add to cart from other pages, we still need to invalidate the cart page cart
        invalidateEntireCart();
      }
      if (!isOnCartPage) {
        // We need to clear the Router cache so that the RSC payload is refetched
        setAddedToBagStore({ shouldRefreshCartPage: true });
      }
    },
    [
      leanCartItemMap,
      invalidateCartItemInventory,
      invalidateLeanBrandsMap,
      invalidateEntireCartSummary,
      invalidateBrandCart,
      invalidateEntireCart,
      isOnCartPage,
      setAddedToBagStore,
      currency,
    ]
  );

  const updateCartItems: UpdateCartItemsHandler = useCallback(
    async ({
      items,
      brand_carts,
      requested_ship_dates,
      allowEmptyItems,
      skipCartRefetch,
      upltParams,
    }: UpdateCartArg) => {
      TelemetryManager.get().startTrace("uplt-add-to-cart");
      if (!allowEmptyItems && !items?.length) {
        return;
      }

      const request: IUpdateCartRequestV2 = IUpdateCartRequestV2.build({
        brand_carts: generateBrandCartForUpdates(
          brand_carts ?? [],
          items,
          requested_ship_dates
        ),
      });

      // TODO: If experiment ships, consider moving the trackTiming in QuickAdd here and use regular mutate instead
      return updateCartMutation([getCartToken(), request], {
        onSuccess: (updateResponse: IUpdateCartResponseV2) => {
          handleUpdateCartSuccess(
            updateResponse,
            upltParams,
            skipCartRefetch,
            request
          );
        },
      });
    },
    [handleUpdateCartSuccess, updateCartMutation]
  );

  const throttledUpdateCartItems: UpdateCartItemsHandler = useMemo(
    () => commonThrottle(updateCartItems),
    [updateCartItems]
  );

  // TODO: This is a temporary wrapper for updateCartItems to fit in UpdateCartHandler,
  // we should be able to delete this when we fully migrate to hooks
  const updateCartItemsWithUpdateHandler = async (
    items: ICartItemForUpdate[],
    options: IUpdateItemsOptions
  ) => {
    const { upltParams } = options;
    await throttledUpdateCartItems({
      items,
      upltParams,
    });
  };

  return {
    ...mutationResult,
    updateCartItems: throttledUpdateCartItems,
    updateCartItemsWithUpdateHandler,
    handleUpdateCartSuccess,
  };
};
